/**
 * HotPads API Client
 *
 * Unified client for API requests to the HotPads backend. The public contract is intentionally
 * small: endpoint modules pass a HotPads API path plus request options, and this module handles
 * SSR/browser routing, request metadata forwarding, response envelopes, and error categorization.
 *
 * Server-side calls hit ZG_API directly by default. Browser calls use `/hotpads-api/api/*`, which
 * Next rewrites to the App Router proxy at `/api/hotpads-api/*`. Middleware can opt into that same
 * proxy route for non-production environments where direct API access is unavailable.
 *
 * Content-Length on POST/PUT/PATCH: this client and the `/api/hotpads-api` proxy send bodies via ky,
 * i.e. the global `fetch`. In the Next server runtime that fetch is patched and re-streams the
 * outgoing Request body, so undici drops the auto-computed Content-Length and switches to
 * `Transfer-Encoding: chunked`. The hotpads-api backend rejects chunked request bodies with a 500
 * (it 500s SSR search and every browser proxy POST alike), so we always pin Content-Length explicitly
 * — see `applyRequestBody`. Raw `fetch(url, { body })` (e.g. oauth-exchange) keeps its Content-Length
 * and is unaffected; only ky's Request path needs this. (HP-8173)
 */

import { type Client, getClient as getHttpClient, HTTPError, type Options } from '@zg-rentals/http-client';
import { logHttpRequestFailure } from './httpErrorLogging';
import { getServerApiBaseUrl } from './serverApiTarget';
import { getServerRequestContext } from './serverRequestContext';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiServerRouting = 'direct' | 'proxy';

export type ApiClientOptions = {
  method?: ApiMethod;
  path: string;
  queryParams?: Record<string, unknown>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeout?: number;
  ip?: string;
  userAgent?: string;
  buildId?: string;
  serverRouting?: ApiServerRouting;
  proxyOrigin?: string;
  followApiRedirects?: boolean;
  /** Suppresses transport logging only; the parsed ApiError is still thrown to the caller. */
  isExpectedError?: (error: ApiError) => boolean;
};

export type ApiClientConfig = {
  ip?: string;
  userAgent?: string;
  buildId?: string;
  headers?: Record<string, string>;
  serverRouting?: ApiServerRouting;
  proxyOrigin?: string;
};

const DEFAULT_TIMEOUT_MS = 30000;
const BROWSER_PROXY_BASE_PATH = '/hotpads-api/api';
const PX_BLOCKED_HEADER = 'x-px-blocked';

export class ApiError extends Error {
  status?: number;
  statusText?: string;
  responseData?: unknown;
  category?: string;

  constructor(message: string, details?: { status?: number; statusText?: string; data?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = details?.status;
    this.statusText = details?.statusText;
    this.responseData = details?.data;
  }
}

export class ApiRedirectError extends Error {
  location: string;
  status: number;

  constructor(location: string, status: number) {
    super(`API request redirected to ${location}`);
    this.name = 'ApiRedirectError';
    this.location = location;
    this.status = status;
  }
}

export function isPerimeterXBlockedError(error: Error): boolean {
  return error instanceof ApiError && error.category === 'PX_BLOCKED_ERROR';
}

export function isRequestTimeoutError(error: Error): boolean {
  return error instanceof ApiError && error.category === 'TIMEOUT_ERROR';
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function trimLeadingSlash(value: string): string {
  return value.replace(/^\/+/, '');
}

function buildUrl(baseUrl: string, path: string): string {
  return `${trimTrailingSlash(baseUrl)}/${trimLeadingSlash(path)}`;
}

function getProxyBaseUrl(proxyOrigin: string | undefined): string {
  if (!proxyOrigin) {
    throw new ApiError('proxyOrigin is required for server proxy API routing');
  }

  return trimTrailingSlash(new URL(BROWSER_PROXY_BASE_PATH, proxyOrigin).toString());
}

function getApiBaseUrl(config: ApiClientConfig): string {
  if (typeof window !== 'undefined') {
    return BROWSER_PROXY_BASE_PATH;
  }

  if (config.serverRouting === 'proxy') {
    return getProxyBaseUrl(config.proxyOrigin);
  }

  return getServerApiBaseUrl();
}

function createHotpadsRequestHeaders({
  buildId = '',
  headers = {},
  ip = '',
  isNode,
  userAgent = '',
}: {
  buildId?: string;
  headers?: Record<string, string>;
  ip?: string;
  isNode: boolean;
  userAgent?: string;
}): Record<string, string> {
  const nodeHeaders: Record<string, string> = isNode
    ? {
        'user-agent': userAgent,
        'x-brand': 'hotpads',
        'x-client-ip': ip,
      }
    : {};

  return {
    Accept: '*/*',
    'x-build-id': buildId,
    ...nodeHeaders,
    ...headers,
  };
}

// Static generation and ISR revalidation run outside any request scope, so there is no client
// UA to forward. Identify the caller instead of sending a blank UA so hotpads-api logs can
// attribute this traffic. Only applies when no request context exists at all — a real inbound
// request that omits its UA header keeps the empty value rather than being misattributed as
// internal traffic. (HP-8625)
export function getServerFallbackUserAgent(): string {
  const phase = process.env.NEXT_PHASE === 'phase-production-build' ? 'build' : 'server';
  return `hotpads-web-next/${phase}`;
}

function resolveServerUserAgent(
  configUserAgent: string | undefined,
  requestContext: ReturnType<typeof getServerRequestContext>,
): string {
  if (configUserAgent) return configUserAgent;
  if (requestContext) return requestContext.userAgent ?? '';
  return getServerFallbackUserAgent();
}

function createHttpClient(config: ApiClientConfig = {}): Client {
  const requestContext = typeof window === 'undefined' ? getServerRequestContext() : undefined;
  const forwardedHeaders: Record<string, string> = {};

  if (typeof window === 'undefined' && requestContext?.cookieHeader) {
    forwardedHeaders.cookie = requestContext.cookieHeader;
  }

  if (typeof window === 'undefined' && requestContext?.requestId) {
    forwardedHeaders['x-request-id'] = requestContext.requestId;
  }

  const headers = createHotpadsRequestHeaders({
    ip: config.ip || requestContext?.ip || '',
    userAgent: resolveServerUserAgent(config.userAgent, requestContext),
    isNode: typeof window === 'undefined',
    buildId: config.buildId || '',
    headers: {
      ...forwardedHeaders,
      ...config.headers,
    },
  });

  return getHttpClient().extend({
    headers,
    retry: 0,
    throwHttpErrors: false,
  });
}

function toSearchParams(queryParams: Record<string, unknown>): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      searchParams.set(key, value.join(','));
      return;
    }

    searchParams.set(key, String(value));
  });

  return searchParams;
}

function methodSupportsBody(method: ApiMethod): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH';
}

function isBodyInit(value: unknown): value is BodyInit {
  if (typeof value === 'string') return true;
  if (value instanceof ArrayBuffer) return true;
  if (ArrayBuffer.isView(value)) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (typeof FormData !== 'undefined' && value instanceof FormData) return true;
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) return true;
  if (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream) return true;
  return false;
}

function bodyByteLength(value: BodyInit): number | undefined {
  if (typeof value === 'string') return new TextEncoder().encode(value).byteLength;
  if (value instanceof ArrayBuffer) return value.byteLength;
  if (ArrayBuffer.isView(value)) return value.byteLength;
  return undefined;
}

// Serializes the request body and pins Content-Length. In the Next server runtime the patched
// global `fetch` re-streams the outgoing body, so undici drops the auto-computed Content-Length
// and falls back to `Transfer-Encoding: chunked` — which the hotpads-api backend rejects with a
// 500. Setting Content-Length explicitly keeps every POST/PUT/PATCH fixed-length. We serialize
// JSON here (rather than ky's `json` option) so the byte length is exact. (HP-8173)
function applyRequestBody(options: Options, headers: Record<string, string>, method: ApiMethod, body: unknown): void {
  if (!methodSupportsBody(method) || body === undefined) {
    return;
  }

  if (isBodyInit(body)) {
    options.body = body;
    const length = bodyByteLength(body);
    if (length !== undefined) headers['content-length'] = String(length);
    return;
  }

  const serialized = JSON.stringify(body);
  options.body = serialized;
  headers['content-type'] = 'application/json';
  headers['content-length'] = String(new TextEncoder().encode(serialized).byteLength);
}

async function parseResponseData(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 304) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.toLowerCase().includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

type RequestInfo = { method: string; url: string; latencyMs?: number; signal?: AbortSignal };

function throwPerimeterXError(
  response: Response,
  requestInfo: RequestInfo,
  isExpectedError?: (error: ApiError) => boolean,
): never {
  const pxError = new ApiError('Request blocked by PerimeterX', {
    status: response.status,
    statusText: response.statusText,
  });
  pxError.category = 'PX_BLOCKED_ERROR';
  if (!isExpectedError?.(pxError)) {
    logHttpRequestFailure(pxError, { ...requestInfo, status: response.status, headers: response.headers });
  }
  throw pxError;
}

async function executeRequest<T>(
  client: Client,
  url: string,
  options: Options,
  isExpectedError?: (error: ApiError) => boolean,
): Promise<{ data: T; status: number; statusText: string }> {
  const method = options.method ?? 'GET';
  const startTime = Date.now();
  const response = await client(url, options);
  const latencyMs = Date.now() - startTime;
  const data = await parseResponseData(response);

  if (response.headers.get(PX_BLOCKED_HEADER)) {
    throwPerimeterXError(response, { method, url, latencyMs }, isExpectedError);
  }

  if (response.status >= 300 && response.status < 400) {
    const location = response.headers.get('location');
    if (location) {
      throw new ApiRedirectError(location, response.status);
    }
  }

  if (response.status >= 400) {
    const apiError = new ApiError(`API request failed with status ${response.status}`, {
      status: response.status,
      statusText: response.statusText,
      data,
    });
    // 4xx is usually an expected application outcome (not found, unauthorized, bad input) that the
    // caller already handles via the thrown ApiError — logging it as an error on every occurrence
    // would swamp Splunk/Dragnet with routine traffic and risks tripping alerts. Most 5xx responses
    // are genuine backend failures; endpoint-specific predicates cover known semantic errors that
    // a backend currently reports with the wrong HTTP status.
    if (response.status >= 500 && !isExpectedError?.(apiError)) {
      logHttpRequestFailure(apiError, { method, url, latencyMs, status: response.status, headers: response.headers });
    }
    throw apiError;
  }

  if (data === undefined || data === null) {
    return { data: null as T, status: response.status, statusText: response.statusText };
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return { data: data as T, status: response.status, statusText: response.statusText };
  }

  const responseData = data as Record<string, unknown>;
  if (responseData.error) {
    const errorMessage = typeof responseData.error === 'string' ? responseData.error : 'API Error';
    const apiError = new ApiError(errorMessage, {
      status: response.status,
      statusText: response.statusText,
      data,
    });
    apiError.category = 'API_DATA_ERROR';
    // Always a 2xx HTTP status carrying an application-level error in the body — typically a
    // validation failure, not a systemic problem. Same reasoning as the 4xx case above.
    throw apiError;
  }

  return { data: responseData as T, status: response.status, statusText: response.statusText };
}

async function handleHttpClientError(
  error: unknown,
  requestInfo: RequestInfo,
  isExpectedError?: (error: ApiError) => boolean,
): Promise<never> {
  if (error instanceof ApiError || error instanceof ApiRedirectError) {
    throw error;
  }

  if (isRequestCancellation(error, requestInfo.signal)) {
    throw error;
  }

  if (error instanceof HTTPError) {
    const response = error.response;
    if (response.headers.get(PX_BLOCKED_HEADER)) {
      throwPerimeterXError(response, requestInfo, isExpectedError);
    }

    const apiError = new ApiError(`API request failed with status ${response.status}`, {
      status: response.status,
      statusText: response.statusText,
      data: await parseResponseData(response),
    });
    if (response.status >= 500 && !isExpectedError?.(apiError)) {
      logHttpRequestFailure(apiError, { ...requestInfo, status: response.status, headers: response.headers });
    }
    throw apiError;
  }

  if (error instanceof Error) {
    const networkError = new ApiError(`Network request failed: ${error.message}`, {});
    networkError.category = error.name === 'TimeoutError' ? 'TIMEOUT_ERROR' : 'NETWORK_ERROR';
    if (!isExpectedError?.(networkError)) {
      logHttpRequestFailure(networkError, requestInfo);
    }
    throw networkError;
  }

  throw error;
}

function isRequestCancellation(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const code = (error as Error & { code?: unknown }).code;
  if (error.name === 'AbortError' || error.name === 'CanceledError' || code === 'ERR_CANCELED') {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes('signal is aborted') ||
    message.includes('fetch is aborted') ||
    message.includes('user aborted a request') ||
    message.includes('the operation was aborted') ||
    message.includes('this operation was aborted')
  );
}

async function makeApiRequest<T = unknown>(
  client: Client,
  path: string,
  options: {
    method?: ApiMethod;
    queryParams?: Record<string, unknown>;
    body?: unknown;
    signal?: AbortSignal;
    headers?: Record<string, string>;
    timeout?: number;
    serverRouting?: ApiServerRouting;
    proxyOrigin?: string;
    followApiRedirects?: boolean;
    isExpectedError?: (error: ApiError) => boolean;
  } = {},
): Promise<T> {
  const { method = 'GET', queryParams = {}, body, signal, headers, timeout, isExpectedError } = options;
  const url = buildUrl(
    getApiBaseUrl({
      serverRouting: options.serverRouting,
      proxyOrigin: options.proxyOrigin,
    }),
    path,
  );
  const searchParams = toSearchParams(queryParams);
  const requestOptions: Options = {
    method,
    searchParams,
    signal,
    timeout: timeout ?? DEFAULT_TIMEOUT_MS,
  };

  // Browser fetch returns opaque redirects with no readable Location, so manual mode is server-only.
  if (options.followApiRedirects === false && typeof window === 'undefined') {
    requestOptions.redirect = 'manual';
  }

  // ky merges per-call `headers` over the client's `.extend()` defaults per-key, so custom
  // headers plus the body's content-type/content-length ride alongside Accept, x-brand,
  // x-client-ip, user-agent, and the forwarded cookie/request-id. Only assign the key when
  // non-empty — passing `headers: undefined` would wipe the defaults instead of merging.
  const outgoingHeaders: Record<string, string> = { ...headers };
  applyRequestBody(requestOptions, outgoingHeaders, method, body);
  if (Object.keys(outgoingHeaders).length > 0) {
    requestOptions.headers = outgoingHeaders;
  }

  try {
    const result = await executeRequest<T>(client, url, requestOptions, isExpectedError);
    return result.data;
  } catch (error) {
    return await handleHttpClientError(error, { method, url, signal }, isExpectedError);
  }
}

let cachedBrowserClient: Client | null = null;

export async function apiClient<T = unknown>({
  method = 'GET',
  path,
  queryParams = {},
  body,
  headers,
  signal,
  timeout,
  ip,
  userAgent,
  buildId,
  serverRouting,
  proxyOrigin,
  followApiRedirects,
  isExpectedError,
}: ApiClientOptions): Promise<T> {
  const isServer = typeof window === 'undefined';

  const client = isServer
    ? createHttpClient({ ip, userAgent, buildId, headers, serverRouting, proxyOrigin })
    : (cachedBrowserClient ??= createHttpClient({ ip, userAgent, buildId, headers }));

  return makeApiRequest<T>(client, path, {
    method,
    queryParams,
    body,
    signal,
    headers,
    timeout,
    serverRouting,
    proxyOrigin,
    followApiRedirects,
    isExpectedError,
  });
}
